/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo } from 'react';
import { ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation, type NavigationProp, type ParamListBase } from '@react-navigation/native';
import { settingsStyles as styles } from '../../../settings/screens/settingsScreen.styles';
import { DenseListCard } from '../../../shared/surface-cards/DenseListCard'
import { EmbeddedMetric } from '../../../shared/surface-cards/EmbeddedMetric'
import { EmbeddedMetricGrid } from '../../../shared/surface-cards/EmbeddedMetricGrid'
import { StatusPill } from '../../../shared/surface-cards/StatusPill';
import { TicketListRow } from '../../../shared/ticket/TicketListRow';
import { formatRelativeDayTime } from '@shared/vueltoDateTime';
import { CashOutflowsSection } from '../CashOutflowsSection';
import { CashActivityRow } from '../CashActivityList';
import { CashCollapsibleSection } from '../CashCollapsibleSection';
import type { CashVm } from './cashManagement.types';
import { CashTurnoSummaryBody } from './CashTurnoSummaryBody';
import { CashOpenTurnoForm } from './CashOpenTurnoForm';
import {
  formatMoney,
  formatCashArqueoResult,
  formatCashDrawerBreakdown,
  formatSalesCount,
  formatTurnoResumenSubtitle,
} from './cashManagement.utils';
import { CashLastClosedSummary } from './CashDetailPanels';
import { cashArqueoCopy, cashNonCashTodayLabel } from './cashArqueoCopy';
import { formatTurnoResumenTitle } from './cashCloseTurnoPresentation';
import { cashPanelStyles as panelStyles } from './cashManagement.panelStyles';
import { cashStyles } from './cashManagement.styles';
import { spacing } from '../../../shared/theme/spacing'
import { webOperationalScrollBindings } from '../../../shared/theme/webScrollbar';
import { ticketColors } from '../../../shared/theme/ticketColors';

type Props = {
  vm: CashVm;
  layoutWide: boolean;
  onOpenLastClosed?: () => void;
  /** Link "Ver egresos" en record page — lleva a la sección Movimientos del workspace. */
  onOpenMovements?: () => void;
  /** Caja cerrada desktop: teaser de actividad lleva a la sección Actividad. */
  onOpenActivity?: () => void;
  /** Cuando el padre ya provee scroll (workspace desktop). */
  embeddedScroll?: boolean;
  /** Record page con header arriba: sin card duplicada, columna única. */
  recordPageMode?: boolean;
};

function TurnoKpiTile({
  label,
  value,
  sub,
  warn,
}: {
  label: string;
  value: string;
  sub?: string;
  warn?: boolean;
}) {
  return (
    <View
      style={[panelStyles.turnoKpiTile, warn && panelStyles.turnoKpiTileWarn]}
      accessible
      accessibilityRole="text"
      accessibilityLabel={`${label}: ${value}${sub ? ` · ${sub}` : ''}`}
    >
      <Text style={panelStyles.turnoKpiTileLabel}>{label}</Text>
      <Text
        style={[panelStyles.turnoKpiTileValue, warn && panelStyles.turnoKpiTileValueWarn]}
        numberOfLines={1}
      >
        {value}
      </Text>
      {sub ? (
        <Text style={panelStyles.turnoKpiTileSub} numberOfLines={1}>
          {sub}
        </Text>
      ) : null}
    </View>
  );
}

function TurnoMetaGrid({ vm, openingAmount }: { vm: CashVm; openingAmount: number }) {
  const activeSession = vm.cashSummary?.activeSession;
  if (!activeSession) return null;

  const cells = [
    { label: 'Apertura', value: formatRelativeDayTime(activeSession.openedAt) },
    { label: 'Fondo inicial', value: formatMoney(openingAmount) },
    vm.activeSessionOpenerLabel ? { label: 'Operador', value: vm.activeSessionOpenerLabel } : null,
    vm.turnOpenDurationLabel ? { label: 'Duración', value: vm.turnOpenDurationLabel } : null,
  ].filter(Boolean) as Array<{ label: string; value: string }>;

  return (
    <EmbeddedMetricGrid gridStyle={{ gap: spacing.sm }}>
      {cells.map((c) => (
        <EmbeddedMetric key={c.label} label={c.label} value={c.value} minWidth={100} />
      ))}
    </EmbeddedMetricGrid>
  );
}

export function CashTurnoWorkspacePanel({
  vm,
  layoutWide,
  onOpenLastClosed,
  onOpenMovements,
  onOpenActivity,
  embeddedScroll,
  recordPageMode,
}: Props) {
  const navigation = useNavigation();
  const activeSession = vm.cashSummary?.activeSession;
  const openingAmount = activeSession?.openingAmount ?? 0;
  const sessionCashTotal = vm.cashSummary?.sessionCashTotal ?? 0;
  const sessionInflowsTotal = vm.cashSummary?.sessionInflowsTotal ?? 0;
  const sessionCashSalesTotal = sessionCashTotal - sessionInflowsTotal;
  const sessionOutflowsTotal = vm.cashSummary?.sessionOutflowsTotal ?? 0;
  const sessionSalesCount = vm.cashSummary?.sessionSalesCount ?? 0;
  const sessionSalesTotal = vm.cashSummary?.sessionSalesTotal ?? 0;
  const todaySalesCount = vm.cashSummary?.todaySalesCount ?? 0;
  const todaySalesTotal = vm.cashSummary?.todaySalesTotal ?? 0;
  const todayCashTotal = vm.cashSummary?.todayCashTotal ?? 0;
  const todayDigitalTotal = vm.cashSummary?.todayDigitalTotal ?? 0;
  const lastClosed = vm.cashSummary?.lastClosedSession;
  const lastClosedDiff = lastClosed?.difference ?? 0;
  const nonCashLabel = cashNonCashTodayLabel(vm.customerModuleEnabled);
  const hideOutflowRegister = Boolean(embeddedScroll || recordPageMode);

  const resumenTitle = formatTurnoResumenTitle(activeSession?.openedAt);

  // Caja cerrada en desktop: el teaser de actividad necesita el feed cargado.
  useEffect(() => {
    if (!activeSession && recordPageMode && vm.canView) {
      void vm.activityFlow?.ensureLoaded();
    }
  }, [activeSession, recordPageMode, vm]);

  const sessionMovements = vm.outflowsFlow?.outflows ?? [];
  const movementsOutCount = sessionMovements.filter(
    (o) => o.status === 'active' && o.direction === 'out',
  ).length;
  const movementsInCount = sessionMovements.filter(
    (o) => o.status === 'active' && o.direction === 'in',
  ).length;
  const movementsTeaserSub = `${movementsOutCount} ${
    movementsOutCount === 1 ? 'egreso' : 'egresos'
  } · ${movementsInCount} ${movementsInCount === 1 ? 'ingreso' : 'ingresos'} · Egresado ${formatMoney(
    sessionOutflowsTotal,
  )} · Ingresado ${formatMoney(sessionInflowsTotal)}`;
  const hasMovementsTeaser = Boolean(
    recordPageMode && vm.cashOutflowsEnabled && onOpenMovements,
  );

  const movementsTeaser = hasMovementsTeaser ? (
    <TouchableOpacity
      style={[panelStyles.teaserRow, panelStyles.teaserRowDivider]}
      onPress={onOpenMovements}
      accessibilityRole="button"
      accessibilityLabel="Ver movimientos del turno"
      testID="caja.movements.open"
    >
      <View style={panelStyles.teaserTextCol}>
        <Text style={panelStyles.teaserTitle}>Movimientos del turno</Text>
        <Text style={panelStyles.teaserSubtitle} numberOfLines={1}>
          {movementsTeaserSub}
        </Text>
      </View>
      <Ionicons name="chevron-forward" size={20} color={ticketColors.ink2} />
    </TouchableOpacity>
  ) : null;

  const lastClosedBlock = useMemo(() => {
    if (!lastClosed) return null;
    if (onOpenLastClosed) {
      const firstInCard = recordPageMode && !hasMovementsTeaser;
      return (
        <TouchableOpacity
          style={[panelStyles.teaserRow, firstInCard && panelStyles.teaserRowDivider]}
          onPress={onOpenLastClosed}
          accessibilityRole="button"
          accessibilityLabel="Último cierre"
        >
          <View style={panelStyles.teaserTextCol}>
            <Text style={panelStyles.teaserTitle}>Último cierre</Text>
            <Text style={panelStyles.teaserSubtitle} numberOfLines={2}>
              {`${lastClosed.closedAt ? formatRelativeDayTime(lastClosed.closedAt) : '—'}${
                vm.lastClosedByLabel ? ` · Por ${vm.lastClosedByLabel}` : ''
              } · ${formatCashArqueoResult(lastClosedDiff)}`}
            </Text>
          </View>
          {lastClosedDiff !== 0 ? (
            <StatusPill label="Con diferencia" tone="warning" compact />
          ) : null}
          <Ionicons name="chevron-forward" size={20} color={ticketColors.ink2} />
        </TouchableOpacity>
      );
    }
    return <CashLastClosedSummary vm={vm} session={lastClosed} />;
  }, [lastClosed, lastClosedDiff, onOpenLastClosed, vm, recordPageMode, hasMovementsTeaser]);

  const resumenSection = activeSession ? (
    <CashCollapsibleSection
      title={resumenTitle}
      subtitle={formatTurnoResumenSubtitle(sessionSalesCount, sessionSalesTotal, todaySalesCount)}
      defaultExpanded={false}
      brightTitle
    >
      <CashTurnoSummaryBody vm={vm} />
    </CashCollapsibleSection>
  ) : null;

  // Desktop workspace (recordPageMode): hero "Debería haber" + grilla KPI —
  // misma estructura que el proto v-main.
  const turnoHeroBlock =
    recordPageMode && vm.expectedCashInDrawer != null ? (
      <View style={[cashStyles.workspaceTabCard, panelStyles.turnoHeroCard]}>
        <Text style={panelStyles.turnoHeroLabel}>Debería haber en caja</Text>
        <Text style={panelStyles.turnoHeroAmount}>{formatMoney(vm.expectedCashInDrawer)}</Text>
        <Text style={panelStyles.turnoHeroBreakdown}>
          {formatCashDrawerBreakdown(openingAmount, sessionCashSalesTotal, {
            outflowsTotal: sessionOutflowsTotal,
            showOutflows: vm.cashOutflowsEnabled,
            inflowsTotal: sessionInflowsTotal,
            showInflows: vm.cashOutflowsEnabled,
          })}
        </Text>
        <Text style={panelStyles.turnoHeroSub}>
          {`${formatSalesCount(sessionSalesCount)} · ${formatMoney(sessionSalesTotal)} en el turno · Efectivo fuera del turno ${formatMoney(vm.cashOutsideSession ?? 0)}`}
        </Text>
      </View>
    ) : null;

  const lastClosedKpiValue =
    lastClosedDiff === 0
      ? formatMoney(0)
      : `${lastClosedDiff > 0 ? '+' : '−'}${formatMoney(Math.abs(lastClosedDiff))}`;

  const turnoKpiGrid = recordPageMode ? (
    <View style={panelStyles.turnoKpiGrid}>
      <TurnoKpiTile
        label={cashArqueoCopy.sessionSalesCountLabel}
        value={formatSalesCount(sessionSalesCount)}
        sub={formatMoney(sessionSalesTotal)}
      />
      <TurnoKpiTile
        label="Hoy en sucursal"
        value={formatMoney(todaySalesTotal)}
        sub={formatSalesCount(todaySalesCount)}
      />
      <TurnoKpiTile label={nonCashLabel} value={formatMoney(todayDigitalTotal)} />
      {lastClosed ? (
        <TurnoKpiTile
          label="Último cierre"
          value={lastClosedKpiValue}
          sub={`${lastClosed.closedAt ? formatRelativeDayTime(lastClosed.closedAt) : '—'} · ${
            lastClosedDiff === 0 ? 'cuadra' : lastClosedDiff < 0 ? 'faltante' : 'sobrante'
          }`}
          warn={lastClosedDiff !== 0}
        />
      ) : null}
    </View>
  ) : null;

  const activeMainBody = activeSession ? (
    <>
      {!recordPageMode ? <TurnoMetaGrid vm={vm} openingAmount={openingAmount} /> : null}
      {!recordPageMode && vm.expectedCashInDrawer != null ? (
        <View style={panelStyles.cashHeroBand}>
          <Text style={panelStyles.cashHeroKicker}>Desglose efectivo</Text>
          <Text style={panelStyles.cashHeroSubtitle}>
            {formatCashDrawerBreakdown(openingAmount, sessionCashSalesTotal, {
              outflowsTotal: sessionOutflowsTotal,
              showOutflows: vm.cashOutflowsEnabled,
              inflowsTotal: sessionInflowsTotal,
              showInflows: vm.cashOutflowsEnabled,
            })}
          </Text>
        </View>
      ) : null}
      {!recordPageMode ? (
        <EmbeddedMetricGrid dividerTop gridStyle={{ gap: spacing.sm }}>
          <EmbeddedMetric label={cashArqueoCopy.sessionSalesCountLabel} value={formatSalesCount(sessionSalesCount)} minWidth={88} />
          <EmbeddedMetric
            label={cashArqueoCopy.sessionSalesAmountLabel}
            value={formatMoney(sessionSalesTotal)}
            minWidth={128}
            fitValue
            style={{ flex: 1.35 }}
          />
          {vm.cashOutflowsEnabled ? (
            <EmbeddedMetric
              label="Egresos"
              value={formatMoney(sessionOutflowsTotal)}
              minWidth={96}
              fitValue
            />
          ) : null}
        </EmbeddedMetricGrid>
      ) : null}
      {vm.cashOutflowsEnabled && !recordPageMode ? (
        <CashOutflowsSection
          vm={vm.outflowsFlow}
          canEdit={vm.canEdit}
          canVoid={vm.canShowOutflowVoid}
          sessionOutflowsTotal={sessionOutflowsTotal}
          operatorLabel={vm.resolveOperatorLabel}
          layoutWide={layoutWide}
          expensesModuleEnabled={vm.expensesModuleEnabled}
          expenseRegisterSuppliers={vm.expenseRegisterSuppliers}
          activeCashSessionInfo={vm.activeCashSessionInfo}
          expenseRegisterSubmitting={vm.expenseRegisterSubmit.submitting}
          onExpenseRegisterSubmit={vm.expenseRegisterSubmit.handleSubmit}
          onOpenRegister={vm.handleOpenRegisterOutflow}
          onOpenRegisterInflow={vm.handleOpenRegisterInflow}
          compactList
          hideRegisterButton={hideOutflowRegister}
        />
      ) : null}
    </>
  ) : null;

  const todayAllZero =
    todaySalesCount === 0 && todaySalesTotal === 0 && todayCashTotal === 0 && todayDigitalTotal === 0;

  const closedMetricsBlock =
    !activeSession && !todayAllZero ? (
      <EmbeddedMetricGrid gridStyle={{ gap: spacing.sm }}>
        <EmbeddedMetric label="Ventas hoy" value={formatSalesCount(todaySalesCount)} minWidth={100} />
        <EmbeddedMetric label="Monto hoy" value={formatMoney(todaySalesTotal)} minWidth={100} />
        <EmbeddedMetric label="Efectivo hoy" value={formatMoney(todayCashTotal)} minWidth={100} />
        <EmbeddedMetric label={nonCashLabel} value={formatMoney(todayDigitalTotal)} minWidth={100} />
      </EmbeddedMetricGrid>
    ) : null;

  const activityItems = vm.activityFlow?.items ?? [];
  const closedActivityBlock =
    !activeSession && recordPageMode && vm.canView ? (
      <View style={cashStyles.workspaceTabCardFlush}>
        <Text style={panelStyles.closedListHeader}>Actividad reciente</Text>
        {activityItems.slice(0, 5).map((item, idx, arr) => (
          <CashActivityRow
            key={`${item.kind}:${item.id}:${item.occurredAt}`}
            item={item}
            operatorLabel={vm.resolveOperatorLabel}
            group={!onOpenActivity && idx === arr.length - 1 ? 'groupedLast' : 'grouped'}
            layoutWide
          />
        ))}
        {!vm.activityFlow?.loading && activityItems.length === 0 ? (
          <TicketListRow
            group={onOpenActivity ? 'grouped' : 'groupedLast'}
            title="Sin movimientos recientes"
            subtitle="Los movimientos de caja de la sucursal aparecen acá"
          />
        ) : null}
        {onOpenActivity ? (
          <TicketListRow
            group="groupedLast"
            title="Ver toda la actividad"
            linkLabel="Ver"
            showChevron
            onPress={onOpenActivity}
            accessibilityLabel="Actividad de la sucursal"
            testID="caja.activity.open"
          />
        ) : null}
      </View>
    ) : null;

  const closedMainBody = !activeSession ? (
    <>
      {!recordPageMode ? closedMetricsBlock : null}
      <CashOpenTurnoForm
        vm={vm}
        footer={
          vm.cashOutflowsEnabled && vm.expensesModuleEnabled ? (
            <View style={panelStyles.closedCashOutflowsHint}>
              <Text style={styles.caption}>{cashArqueoCopy.closedCashExpensesHint}</Text>
              <TouchableOpacity
                onPress={() => (navigation as NavigationProp<ParamListBase>).navigate('Expenses')}
                accessibilityRole="button"
              >
                <Text style={panelStyles.hintLink}>Ir a Gastos</Text>
              </TouchableOpacity>
            </View>
          ) : null
        }
      />
    </>
  ) : null;

  const recordPageStack = (
    <View style={[cashStyles.recordPagePanel, { gap: spacing.sm }]}>
      {activeSession ? (
        <>
          {turnoHeroBlock}
          {turnoKpiGrid}
          {movementsTeaser || lastClosedBlock ? (
            <View style={cashStyles.workspaceTabCard}>
              {movementsTeaser}
              {lastClosedBlock}
            </View>
          ) : null}
        </>
      ) : (
        // Caja cerrada desktop (proto 1b): contexto a lo ancho en list cards —
        // "Hoy en sucursal" + "Último cierre" en grid2, actividad full-width.
        // La apertura va como modal desde el CTA del header (CashWorkspaceScreen).
        <>
          <View style={panelStyles.closedGridDesktop}>
            <View style={panelStyles.closedGridMain}>
              <View style={cashStyles.workspaceTabCardFlush}>
                <Text style={panelStyles.closedListHeader}>Hoy en sucursal</Text>
                <TicketListRow
                  group="grouped"
                  title="Ventas hoy"
                  subtitle="En la sucursal, todos los medios"
                  value={formatSalesCount(todaySalesCount)}
                />
                <TicketListRow
                  group="groupedLast"
                  title="Monto hoy"
                  subtitle={`Efectivo + ${nonCashLabel.replace(' hoy', '').toLowerCase()}`}
                  value={formatMoney(todaySalesTotal)}
                />
              </View>
            </View>
            <View style={panelStyles.closedGridSide}>
              <View style={cashStyles.workspaceTabCardFlush}>
                <Text style={panelStyles.closedListHeader}>Último cierre</Text>
                {lastClosed ? (
                  <>
                    <TicketListRow
                      group={onOpenLastClosed ? 'grouped' : 'groupedLast'}
                      title={`${
                        lastClosed.closedAt ? formatRelativeDayTime(lastClosed.closedAt) : '—'
                      }${vm.lastClosedByLabel ? ` · Por ${vm.lastClosedByLabel}` : ''}`}
                      subtitle={`Debería ${formatMoney(lastClosed.expectedAmount ?? 0)} · Contado ${formatMoney(lastClosed.closingAmount ?? 0)}`}
                      trailingPills={[
                        lastClosedDiff === 0
                          ? { label: cashArqueoCopy.noDifference, tone: 'muted' }
                          : {
                              label: `${lastClosedDiff > 0 ? '+' : '−'}${formatMoney(Math.abs(lastClosedDiff))}`,
                              tone: 'warn',
                            },
                      ]}
                    />
                    {onOpenLastClosed ? (
                      <TicketListRow
                        group="groupedLast"
                        title="Ver arqueo completo"
                        subtitle="Desglose por medio de pago y nota de cierre"
                        linkLabel="Ver"
                        showChevron
                        onPress={onOpenLastClosed}
                        accessibilityLabel="Ver arqueo del último cierre"
                      />
                    ) : null}
                  </>
                ) : (
                  <TicketListRow
                    group="groupedLast"
                    title="Sin cierres todavía"
                    subtitle="El próximo cierre de turno aparece acá"
                  />
                )}
              </View>
            </View>
          </View>
        </>
      )}
      {closedActivityBlock}
    </View>
  );

  const stackedBody = activeSession ? (
    <>
      {activeMainBody}
      {!recordPageMode ? (
        <>
          {resumenSection}
          {lastClosedBlock}
        </>
      ) : null}
    </>
  ) : (
    <>
      {closedMainBody}
      {!recordPageMode ? lastClosedBlock : null}
    </>
  );

  const body = recordPageMode ? recordPageStack : stackedBody;

  if (embeddedScroll && recordPageMode) {
    return body;
  }

  if (embeddedScroll) {
    return (
      <DenseListCard
        bodyStyle={panelStyles.turnoWorkspaceCardBody}
        accentBarColor={activeSession ? ticketColors.ok : ticketColors.gold}
      >
        <View style={panelStyles.turnoWorkspaceTitleRow}>
          <View style={activeSession ? panelStyles.turnoWorkspaceTitleIconOpen : panelStyles.turnoWorkspaceTitleIconClosed}>
            <Ionicons
              name={activeSession ? 'lock-open-outline' : 'lock-closed-outline'}
              size={20}
              color={activeSession ? ticketColors.ok : ticketColors.gold}
            />
          </View>
          <Text style={panelStyles.turnoWorkspaceCardTitle} accessibilityRole="header">
            {activeSession ? 'Turno en curso' : 'Caja cerrada'}
          </Text>
          <StatusPill label={activeSession ? cashArqueoCopy.statusOpen : cashArqueoCopy.statusClosed} tone={activeSession ? 'success' : 'warning'} compact />
        </View>
        {stackedBody}
      </DenseListCard>
    );
  }

  return (
    <ScrollView
      {...webOperationalScrollBindings(panelStyles.turnoPanelScroll)}
      contentContainerStyle={panelStyles.turnoPanelContent}
      keyboardShouldPersistTaps="handled"
    >
      {stackedBody}
    </ScrollView>
  );
}
